import React, { useState, useCallback, useEffect, useRef } from 'react';
import { trackGuestGrade, trackGuestCapHit } from '../utils/guestEvents';
import GradeCard from './GradeCard';
import { trackGrade } from '../utils/xpixel';
import './PublicScreener.css';

// Logged-out Screener. Powered by the PUBLIC, cached grade endpoint
// (/api/public/grade/{ticker}) — cost-safe, no auth — so cold ad traffic lands
// in a real, working grading product instead of a signup wall. Shares the free-
// grade cap (localStorage) with the hero widget. Save / portfolio / Alpha are
// the signup hooks (per docs/gating-map.md). Rendered by LoginGate for
// anonymous visitors at /screener; authed users get the full StockScreener.
const FREE_LIMIT = 5;
const USE_KEY = 'ag_free_grades';

const readCount = () => {
  const n = parseInt(localStorage.getItem(USE_KEY) || '0', 10);
  return Number.isFinite(n) ? n : 0;
};

const parseTickers = (s) => {
  const seen = new Set();
  const out = [];
  for (const part of (s || '').split(/[,\s]+/)) {
    const t = part.trim().toUpperCase();
    if (t && /^[A-Z][A-Z.\-]{0,7}$/.test(t) && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return out;
};

async function gradeOne(t) {
  try {
    const res = await fetch(`/api/public/grade/${encodeURIComponent(t)}`);
    if (!res.ok) return { ticker: t, error: res.status === 404 ? 'no grade available' : 'error' };
    return await res.json();
  } catch {
    return { ticker: t, error: 'error' };
  }
}

export default function PublicScreener({ onSignIn, onGetStarted }) {
  const [input, setInput] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gated, setGated] = useState(readCount() >= FREE_LIMIT);
  const [notice, setNotice] = useState('');
  // NOT react-router's useSearchParams: LoginGate renders this component
  // OUTSIDE <Router> (App.js wraps the Router inside LoginGate), so that hook
  // threw on render and /screener was a blank page for every logged-out
  // visitor from 2026-09-30 (da356cd) to 2026-11-01. Read the URL directly.
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  // Ref guards against a double run; state drives the copy, because a ref
  // mutation does not re-render and the heading would flash the wrong text.
  const arrivalDone = useRef(false);
  const [arrivedFromShare, setArrivedFromShare] = useState(
    () => parseTickers(searchParams.get('symbols') || '').length > 0,
  );

  const remaining = Math.max(0, FREE_LIMIT - readCount());

  // ?symbols=NVDA — someone arriving from a shared grade card on X. Grade it
  // on arrival so the first thing they see is the REAL card: clickable pillars,
  // the per-test "why" behind every score, and the full price-vs-fundamentals
  // chart with all its metrics and timeframes. The static /grade/{T} page they
  // came from is a hand-built replica that can show none of that.
  //
  // ⚠ Deliberately does NOT consume a free grade, and deliberately ignores the
  // cap. This ticker is already public on the page they just left, so charging
  // for it gives nothing away, and landing a curious visitor straight onto the
  // paywall instead of the thing they clicked to see is how you lose them.
  // Anything they type themselves still counts, and still gates.
  useEffect(() => {
    const raw = searchParams.get('symbols');
    if (!raw || arrivalDone.current) return;
    const tickers = parseTickers(raw).slice(0, 3);
    if (!tickers.length) return;
    arrivalDone.current = true;
    setArrivedFromShare(true);
    try { window.history.replaceState(null, '', window.location.pathname); } catch { /* old browser */ }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const graded = await Promise.all(tickers.map(gradeOne));
        if (cancelled) return;
        setResults(graded);
        graded.filter((g) => !g.error).forEach((g) => trackGuestGrade(g.ticker, 'shared_link'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams]);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (readCount() >= FREE_LIMIT) { trackGuestCapHit('screener'); setGated(true); return; }
    const tickers = parseTickers(input);
    if (!tickers.length) return;
    const allowed = FREE_LIMIT - readCount();

    const toGrade = tickers.slice(0, allowed);
    const overflow = tickers.length - toGrade.length;
    setLoading(true);
    setNotice('');
    try {
      const graded = await Promise.all(toGrade.map(gradeOne));
      const ok = graded.filter((g) => !g.error);
      const newCount = readCount() + ok.length;
      localStorage.setItem(USE_KEY, String(newCount));
      // Mid-funnel conversion signal for X's optimizer (inert until wired up):
      // one event per action that produced at least one real grade.
      if (ok.length) trackGrade();
      // One guest-funnel row per real grade served, so grades_per_visitor is
      // a count of grades and not of submissions (this screener takes several
      // tickers at once).
      ok.forEach((g) => trackGuestGrade(g.ticker, 'screener'));
      // Accumulate so earlier grades stay on screen (new ones on top, deduped).
      setResults((prev) => {
        const fresh = new Set(graded.map((g) => g.ticker));
        return [...graded, ...prev.filter((p) => !fresh.has(p.ticker))];
      });
      setInput('');
      if (overflow > 0) {
        setNotice(`You hit your free-grade limit — sign up to grade the other ${overflow}.`);
      }
      // Gate the instant the limit is reached, not on the next attempt.
      if (newCount >= FREE_LIMIT) { trackGuestCapHit('screener'); setGated(true); }
    } finally {
      setLoading(false);
    }
  }, [input]);

  return (
    <div className="ps">
      <header className="ps-nav">
        <div className="ps-brand">
          <span className="ps-logo">AG</span>
          <span className="ps-brandname">Alpha<b>Grade</b></span>
        </div>
        <div className="ps-nav-actions">
          <button className="ps-signin" onClick={onSignIn}>Sign in</button>
          <button className="ps-getstarted" onClick={onGetStarted}>Get started free</button>
        </div>
      </header>

      <div className="ps-body">
        <a href="/" style={{ display: 'inline-block', marginBottom: '0.85rem', color: '#94A3B8', fontSize: '0.85rem', textDecoration: 'none' }}>
          ← Back to dashboard
        </a>
        <h1 className="ps-title">
          {arrivedFromShare ? 'Here it is, the live version' : 'Grade any stock — free'}
        </h1>
        <p className="ps-sub">
          {arrivedFromShare ? (
            <>
              This is the real card, not a snapshot. Click any of the four
              dimensions to see every test behind the score and why it scored
              that way, and open the chart to run price against revenue, free
              cash flow or earnings over any timeframe. Then grade something of
              your own, free, no account needed.
            </>
          ) : (
            <>
              Type one or more tickers. AlphaGrade scores each across <b>34 individual tests</b> in
              4 dimensions — no account needed.
            </>
          )}
        </p>

        <form className="ps-search" onSubmit={submit}>
          <input
            className="ps-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="e.g. NVDA, AAPL, TSLA"
            aria-label="Enter tickers to grade"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck="false"
          />
          <button className="ps-grade-btn" type="submit" disabled={loading || gated}>
            {loading ? 'Grading…' : 'Grade'}
          </button>
        </form>
        <div className="ps-meter">
          {remaining > 0 ? `${remaining} free grade${remaining === 1 ? '' : 's'} left` : 'Sign up to keep grading'}
        </div>

        {notice && <div className="ps-notice">{notice}</div>}

        <div className="ps-results">
          {results.map((g) => (
            g.error
              ? <div className="ps-err" key={g.ticker}>{g.ticker}: {g.error}</div>
              : <div className="ps-card" key={g.ticker}><GradeCard grade={g} /></div>
          ))}
        </div>

        {gated && (
          <div className="ps-gate">
            <div className="ps-gate-icon">🔓</div>
            <h3>You’ve used your {FREE_LIMIT} free grades</h3>
            <p>Sign up free to keep grading, save a portfolio, build watchlists, and ask Alpha.</p>
            <button className="ps-getstarted lg" onClick={onGetStarted}>Create your free account →</button>
            <div className="ps-gate-note">No credit card required.</div>
          </div>
        )}

        {results.length > 0 && !gated && (
          <div className="ps-upsell">
            <p>Want to <b>save these</b>, track them, and talk to Alpha about your portfolio?</p>
            <button className="ps-getstarted" onClick={onGetStarted}>Create your free account →</button>
          </div>
        )}
      </div>

      <footer className="ps-foot">
        AlphaGrade is for research and education, not financial advice.
      </footer>
    </div>
  );
}
